import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import EmployeesTable from '../../../components/hr/EmployeesTable';
import { Button, Space, Card, Input, Select, Row, Col, Modal, Dropdown } from 'antd';
import {
  PlusOutlined,
  UserAddOutlined,
  ImportOutlined,
  FileExcelOutlined,
  UserOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import { employeesApi, departmentsApi, designationsApi, rolesApi } from '../../../utils/api';
import PermissionGate from '../../../components/PermissionGate';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { translateRoleName } from '../../../utils/formHelpers';
import EmployeeInviteModal from '../../../components/hr/EmployeeInviteModal';
import { useListReturnState } from '../../../hooks/useListReturnState';
import { BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';

const Employees = () => {
  const navigate = useNavigate();
  const { canEdit, canDelete, canEditRecord, canDeleteRecord } = useResourcePermissions('employees');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    gender: '',
    status: '',
    employeeId: '',
    designationId: '',
    departmentId: '',
    roleId: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    genders: [
      { value: 'male', label: 'مرد' },
      { value: 'female', label: 'زن' },
    ],
    statuses: [
      { value: 'active', label: 'فعال' },
      { value: 'deactive', label: 'غیرفعال' },
    ],
    employees: [],
    designations: [],
    departments: [],
    roles: [],
  });
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      filterExpanded,
    }),
    [filters, searchText, filterExpanded],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.filterExpanded != null) setFilterExpanded(restored.filterExpanded);
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // فیلتر خودکار با debounce هنگام تغییر هر فیلتر
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEmployees(searchText);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchText]);

  const fetchFilterOptions = async () => {
    try {
      const [depts, designations, roles, emps] = await Promise.all([
        departmentsApi.getAll(),
        designationsApi.getAll(),
        rolesApi.getAll(),
        employeesApi.getAll(),
      ]);

      setFilterOptions(prev => ({
        ...prev,
        departments: (depts.data || []).map(d => ({ value: d.id, label: d.team_name || d.name })),
        designations: (designations.data || []).map(d => ({ value: d.id, label: d.name })),
        roles: (Array.isArray(roles) ? roles : []).map(r => ({ value: r.id, label: translateRoleName(r.display_name || r.name) })),
        employees: (emps.data || []).map(e => ({ value: e.id, label: e.name })),
      }));
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchEmployees = async (query = '') => {
    setLoading(true);
    try {
      const apiFilters = {};
      const normalizedQuery = query?.trim();
      if (normalizedQuery) apiFilters['users.name'] = normalizedQuery;
      
      // Add other filters
      if (filters.gender) apiFilters.gender = filters.gender;
      if (filters.status) apiFilters.status = filters.status;
      if (filters.employeeId) apiFilters['users.id'] = parseInt(filters.employeeId) || filters.employeeId;
      if (filters.designationId) apiFilters['employee_details.designation_id'] = parseInt(filters.designationId) || filters.designationId;
      if (filters.departmentId) apiFilters['employee_details.department_id'] = parseInt(filters.departmentId) || filters.departmentId;
      if (filters.roleId) apiFilters['roles.id'] = parseInt(filters.roleId) || filters.roleId;

      const res = await employeesApi.getAll(apiFilters);
      const list = (res.data || []).map((item) => {
        const detail = item.employee_detail || item.employeeDetail || {};
        return {
          id: item.id,
          employee_id: detail.employee_id || '--',
          name: item.name || '--',
          email: item.email || '--',
          mobile: item.mobile || null,
          position: detail.designation?.name || null,
          reportsTo: detail.reporting_to?.name || detail.reportingTo?.name || null,
          status: item.status || 'deactive',
          avatar: item.image_url || item.image || null,
          role: Array.isArray(item.roles) && item.roles.length > 0 ? translateRoleName(item.roles[0].display_name || item.roles[0].name) : '--',
        };
      });
      setEmployees(list);
      setTotal(res.meta?.paging?.total || res.data?.length || 0);
    } catch (error) {
      console.error('Error fetching employees:', error);
      message.error('خطا در دریافت لیست کارمندان');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = () => {
    navigate('/hr/employees/add');
  };

  const handleInviteEmployee = () => {
    setInviteModalOpen(true);
  };

  const handleImport = () => {
    navigate('/hr/employees/import');
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await employeesApi.exportList({
        gender: filters.gender || 'all',
        status: filters.status || 'all',
        designation_id: filters.designationId || 'all',
        department_id: filters.departmentId || 'all',
        role_id: filters.roleId || 'all',
        employee_id: filters.employeeId || 'all',
        search: searchText?.trim() || undefined,
      });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export employees:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    try {
      await employeesApi.delete(id);
      message.success('کارمند با موفقیت حذف شد');
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      message.error('خطا در حذف کارمند');
    }
  };

  const handleViewEmployee = (id) => {
    navigateToDetail(`/hr/employees/${id}`);
  };

  const handleEditEmployee = (id) => {
    navigateToDetail(`/hr/employees/${id}/edit`);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      gender: '',
      status: '',
      employeeId: '',
      designationId: '',
      departmentId: '',
      roleId: '',
    });
    setSearchText('');
  };

  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('لطفاً حداقل یک کارمند را انتخاب کنید');
      return;
    }
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف کارمندان</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از حذف {selectedRowKeys.length} کارمند انتخاب‌شده اطمینان دارید؟
        </span>
      ),
      okText: 'بله، حذف کن',
      cancelText: 'لغو',
      okButtonProps: {
        danger: true,
        style: { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' },
      },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await employeesApi.applyQuickAction('delete', selectedRowKeys);
          message.success('کارمندان با موفقیت حذف شدند');
          setSelectedRowKeys([]);
          fetchEmployees(searchText);
        } catch (error) {
          console.error('Error applying bulk delete:', error);
          message.error('خطا در حذف کارمندان');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  const handleBulkStatus = (newStatus) => {
    const label = newStatus === 'active' ? 'فعال' : 'غیرفعال';
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت کارمندان</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از تغییر وضعیت {selectedRowKeys.length} کارمند به «{label}» اطمینان دارید؟
        </span>
      ),
      okText: 'بله',
      cancelText: 'لغو',
      okButtonProps: {
        style: { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' },
      },
      onOk: async () => {
        setBulkActionLoading(true);
        try {
          await employeesApi.applyQuickAction('change-status', selectedRowKeys, newStatus);
          message.success('وضعیت کارمندان بروزرسانی شد');
          setSelectedRowKeys([]);
          fetchEmployees(searchText);
        } catch (error) {
          console.error('Error applying bulk status:', error);
          message.error('خطا در تغییر وضعیت');
        } finally {
          setBulkActionLoading(false);
        }
      },
    });
  };

  const bulkActionMenuItems = [
    ...(canDelete
      ? [
          {
            key: 'delete',
            label: <span style={{ fontFamily: 'CustomFont' }}>حذف انتخاب‌شده‌ها</span>,
            icon: <DeleteOutlined />,
            danger: true,
            onClick: handleBulkDelete,
          },
          { type: 'divider' },
        ]
      : []),
    ...(canEdit
      ? [
          {
            key: 'active',
            label: <span style={{ fontFamily: 'CustomFont' }}>فعال کردن</span>,
            icon: <CheckCircleOutlined className="text-green-600" />,
            onClick: () => handleBulkStatus('active'),
          },
          {
            key: 'deactive',
            label: <span style={{ fontFamily: 'CustomFont' }}>غیرفعال کردن</span>,
            icon: <StopOutlined />,
            onClick: () => handleBulkStatus('deactive'),
          },
        ]
      : []),
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                <UserOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">مدیریت کارمندان</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">مشاهده و مدیریت لیست کارمندان سازمان</p>
              </div>
            </div>
            <Space size="middle" wrap>
              <PermissionGate permission="add_employees">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddEmployee}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افزودن کارمند
                </Button>
              </PermissionGate>
              <PermissionGate permission="add_employees">
                <Button icon={<UserAddOutlined />} onClick={handleInviteEmployee} size="middle" style={{ fontFamily: 'CustomFont' }}>
                  دعوت از کارمندان
                </Button>
              </PermissionGate>
              <PermissionGate permission="add_employees">
                <Button icon={<ImportOutlined />} onClick={handleImport} size="middle" style={{ fontFamily: 'CustomFont' }}>
                  وارد کردن
                </Button>
              </PermissionGate>
              <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} loading={exporting} size="middle" style={{ fontFamily: 'CustomFont' }}>
                خروجی اکسل
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {/* فیلترهای پیشرفته */}
            <div className="mb-6">
              <button
                onClick={() => setFilterExpanded(!filterExpanded)}
                className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FilterOutlined className="text-gray-600 dark:text-gray-400" />
                  <span
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    فیلترهای بیشتر
                  </span>
                </div>
                {filterExpanded ? (
                  <span className="text-gray-400 dark:text-gray-500">▲</span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">▼</span>
                )}
              </button>
              
              {filterExpanded && (
                <div className="mt-4 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                        جنسیت
                      </label>
                      <Select
                        placeholder="همه"
                        value={filters.gender || undefined}
                        onChange={(val) => handleFilterChange('gender', val || '')}
                        allowClear
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        options={filterOptions.genders}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                        وضعیت
                      </label>
                      <Select
                        placeholder="همه"
                        value={filters.status || undefined}
                        onChange={(val) => handleFilterChange('status', val || '')}
                        allowClear
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        options={filterOptions.statuses}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                        سمت شغلی
                      </label>
                      <Select
                        placeholder="همه سمت‌ها"
                        value={filters.designationId || undefined}
                        onChange={(val) => handleFilterChange('designationId', val || '')}
                        allowClear
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        options={filterOptions.designations}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                        دپارتمان
                      </label>
                      <Select
                        placeholder="همه دپارتمان‌ها"
                        value={filters.departmentId || undefined}
                        onChange={(val) => handleFilterChange('departmentId', val || '')}
                        allowClear
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        options={filterOptions.departments}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                        نقش
                      </label>
                      <Select
                        placeholder="همه نقش‌ها"
                        value={filters.roleId || undefined}
                        onChange={(val) => handleFilterChange('roleId', val || '')}
                        allowClear
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        options={filterOptions.roles}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>
                        کارمند
                      </label>
                      <Select
                        placeholder="همه کارمندان"
                        value={filters.employeeId || undefined}
                        onChange={(val) => handleFilterChange('employeeId', val || '')}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        options={filterOptions.employees}
                      />
                    </Col>
                  </Row>
                  <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-gray-100 dark:border-gray-700">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleResetFilters}
                      className="border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-all"
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      ریست فیلترها
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {selectedRowKeys.length > 0 && (canDelete || canEdit) && (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']}>
                  <Button
                    type="primary"
                    loading={bulkActionLoading}
                    size="middle"
                    className="border-0"
                    icon={<DownOutlined />}
                    style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
              </div>
            )}

            {/* جستجو و تعداد */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" style={{ maxWidth: '800px' }}>
              <Input
                placeholder="جستجو نام کارمند..."
                allowClear
                value={searchText}
                onChange={handleSearchChange}
                prefix={<SearchOutlined className="text-gray-400" />}
                style={{ fontFamily: 'CustomFont' }}
              />
              {/* <div className="flex items-center gap-2">
                <Badge count={total} showZero style={{ backgroundColor: '#1890ff' }} />
                <span className="text-gray-600 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
                  تعداد کل
                </span>
              </div> */}
            </div>

            {loading ? (
              <TableSkeleton rows={5} columns={6} />
            ) : (
              <EmployeesTable
                selectedRowKeys={selectedRowKeys}
                onSelectChange={setSelectedRowKeys}
                loading={false}
                data={employees}
                total={total}
                onDelete={handleDeleteEmployee}
                onView={handleViewEmployee}
                onEdit={handleEditEmployee}
                canEditRecord={canEditRecord}
                canDeleteRecord={canDeleteRecord}
              />
            )}
          </div>
        </Card>
      </div>

      <EmployeeInviteModal open={inviteModalOpen} onClose={() => setInviteModalOpen(false)} />
    </MainLayout>
  );
};

export default Employees;
